/**
 * @file index.jsx
 * @brief Renders the compact seller contact and transaction request panel on listing pages.
 */
import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useUnifiedAuth as useAuth } from "../../hooks/useUnifiedAuth";
import { useTranslation } from "react-i18next";
import { createApiClient } from "../../api/axios";
import { useUserProfile } from "../../context/UserProfileContext";
import { getTransactionErrorMessage } from "../../utils/transactionErrors";
import { formatUserDisplayName } from "../../utils/userDisplayName";
import { clampToMaxLength, FIELD_LIMITS } from "../../constants/fieldLimits";
import styles from "./contact-seller.module.css";

/**
 * Drop this component into ListingDetail, passing the listing object.
 * Shows a "Contact seller" button that expands into an inline composer.
 * On send, redirects to the full conversation thread.
 */
export default function ContactSeller({ listing, compact = false }) {
    const { t } = useTranslation();
    const auth        = useAuth();
    const accessToken = auth.user?.access_token;
    const api         = useMemo(
        () => createApiClient({
            isAuthenticated: auth.isAuthenticated,
            user: accessToken ? { access_token: accessToken } : null,
        }),
        [accessToken, auth.isAuthenticated]
    );
    const { profile } = useUserProfile();
    const navigate    = useNavigate();

    const [open, setOpen]     = useState(false);
    const [content, setContent] = useState("");
    const [offerPrice, setOfferPrice] = useState("");
    const [sending, setSending] = useState(false);
    const [error, setError]   = useState(null);
    const [txLoading, setTxLoading] = useState(false);

    const isSold = listing?.status === "sold";
    const listingLocked =
        isSold ||
        listing?.status === "transaction_in_progress" ||
        Boolean(listing?.active_transaction);
    const sellerDisplayName = formatUserDisplayName(listing?.author?.user);

    // Don't show if not authenticated or if user is the author
    if (!auth.isAuthenticated) return null;
    if (profile?.id === listing?.author?.id) return null;

    const createTransaction = async (kind) => {
        if (listingLocked) {
            setError(isSold ? t("contactSeller.listingSold") : t("contactSeller.transactionInProgress"));
            return;
        }

        setError(null);
        setTxLoading(true);
        try {
            const payload = {
                listing: listing.id,
                kind,
            };

            if (kind === "offer") {
                const parsed = Number(offerPrice);
                if (!Number.isFinite(parsed) || parsed <= 0) {
                    setError(t("contactSeller.offerInvalid"));
                    return;
                }
                payload.offered_price = parsed.toFixed(2);
            }

            await api.post("transactions/", payload);
            navigate(`/messages/${listing.id}/${listing.author.id}`);
        } catch (err) {
            setError(getTransactionErrorMessage(t, err, "contactSeller.transactionCreateError"));
            console.error(err);
        } finally {
            setTxLoading(false);
        }
    };

    const handleSend = async () => {
        if (!content.trim()) return;

        if (content.trim().length > FIELD_LIMITS.messageContent) {
            setError(t("messages.messageTooLong", { max: FIELD_LIMITS.messageContent }));
            return;
        }

        setSending(true);
        setError(null);
        try {
            const formData = new FormData();
            formData.append("listing", listing.id);
            formData.append("content", content.trim());
            formData.append("receiver_id", listing.author.id);
            await api.post("messages/", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            navigate(`/messages/${listing.id}/${listing.author.id}`);
        } catch (err) {
            setError(t("contactSeller.sendError"));
            console.error(err);
        } finally {
            setSending(false);
        }
    };

    return (
        <div className={`${styles.wrapper} ${compact ? styles.wrapperCompact : ""}`}>
            <div className={styles.transactionBox}>
                <button
                    className={styles.buyBtn}
                    onClick={() => createTransaction("buy")}
                    disabled={txLoading || listingLocked}
                >
                    {txLoading
                        ? t("contactSeller.sending")
                        : t("contactSeller.buyNow", { price: listing.price })}
                </button>

                <div className={styles.offerRow}>
                    <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={offerPrice}
                        onChange={(e) => setOfferPrice(e.target.value)}
                        placeholder={t("contactSeller.offerPlaceholder")}
                        className={styles.offerInput}
                        disabled={listingLocked || txLoading}
                    />
                    <button
                        className={styles.offerBtn}
                        onClick={() => createTransaction("offer")}
                        disabled={listingLocked || txLoading}
                    >
                        {t("contactSeller.sendOffer")}
                    </button>
                </div>

                {listingLocked && (
                    <p className={styles.lockedHint}>
                        {isSold ? t("contactSeller.listingSold") : t("contactSeller.transactionInProgress")}
                    </p>
                )}
            </div>

            {!open ? (
                <button className={styles.contactBtn} onClick={() => setOpen(true)}>
                    💬 {t("contactSeller.contact")}
                </button>
            ) : (
                <div className={styles.composer}>
                    <div className={styles.composerHeader}>
                        <span className={styles.composerTitle}>{t("contactSeller.messageTo", { name: sellerDisplayName })}</span>
                        <button className={styles.closeBtn} onClick={() => setOpen(false)}>✕</button>
                    </div>

                    <textarea
                        className={styles.textarea}
                        placeholder={t("contactSeller.placeholder", { title: listing.title })}
                        value={content}
                        onChange={(e) => setContent(clampToMaxLength(e.target.value, FIELD_LIMITS.messageContent))}
                        rows={4}
                        autoFocus
                        maxLength={FIELD_LIMITS.messageContent}
                    />

                    {error && <p className={styles.errorMsg}>{error}</p>}

                    <div className={styles.composerActions}>
                        <button className={styles.cancelBtn} onClick={() => setOpen(false)}>
                            {t("common.cancel")}
                        </button>
                        <button
                            className={styles.sendBtn}
                            onClick={handleSend}
                            disabled={sending || !content.trim()}
                        >
                            {sending ? t("contactSeller.sending") : t("contactSeller.send")}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
